import {
  type DocumentData,
  type Firestore,
  collection,
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import { ResourceSchema, ResourceCreateInputSchema, ResourceUpdateInputSchema, type Resource, type ResourceCreateInput, type ResourceUpdateInput } from '@taki/data-model';
import { CURRENT_SCHEMA_VERSION, buildUpdateData, createConverter } from './standard-fields.js';
import { type CountOptions, type ListOptions, runCount, runList } from './list-query.js';
import { createArchiveOps } from './archive-ops.js';

/**
 * Repository de la famille `resource` (capacité interne planifiable, org-wide).
 *
 * Même patron que `asset` (cf. asset dans @taki/data-model) : l'entrée est
 * VALIDÉE par Zod (`ResourceCreateInputSchema`) AVANT toute écriture Firestore.
 * On n'écrit jamais un document pour découvrir ensuite qu'il est invalide.
 */

export type { ResourceCreateInput };

/** Nom de la collection Firestore de la famille resource. */
export const RESOURCES_COLLECTION = 'resources';

/**
 * Repository typé pour la famille `resource`.
 * Toute lecture est validée par `ResourceSchema`, toute écriture par
 * `ResourceCreateInputSchema` (cage IA : aucun accès Firestore direct hors SDK).
 */
export interface ResourceRepository {
  create(input: ResourceCreateInput): Promise<Resource>;
  update(id: string, patch: ResourceUpdateInput): Promise<Resource>;
  list(options?: ListOptions): Promise<Resource[]>;
  count(options?: CountOptions): Promise<number>;
  get(id: string): Promise<Resource | null>;
  /** Archive (`archivedAt`). Semantique commune : cf. archive-ops.ts. */
  archive(id: string): Promise<Resource>;
  /** Desarchive (`archivedAt` = null, JAMAIS deleteField). Cf. archive-ops.ts. */
  unarchive(id: string): Promise<Resource>;
  /** Supprime (`deletedAt` + `archivedAt`), invisible cote client. Cf. archive-ops.ts. */
  remove(id: string): Promise<Resource>;
}

const resourceConverter = createConverter<Resource>((raw: DocumentData) => ResourceSchema.parse(raw));

/**
 * @param getActorId - retourne l'uid de l'utilisateur authentifié courant
 *   (injecté par `createTakiSdk`). Les Security Rules exigent
 *   createdBy/updatedBy == request.auth.uid.
 */
export function createResourceRepository(
  db: Firestore,
  getActorId: () => string,
  orgId: string,
): ResourceRepository {
  const col = collection(db, RESOURCES_COLLECTION).withConverter(resourceConverter);
  // Archiver, desarchiver, supprimer : la semantique des trois etats vit dans
  // `createArchiveOps` (archive-ops.ts), ecrite une seule fois pour les 55
  // familles. Une famille n'a plus rien a decider ici.
  const archiveOps = createArchiveOps<Resource>(db, col, RESOURCES_COLLECTION, getActorId);

  return {
    async create(input: ResourceCreateInput): Promise<Resource> {
      // Valider AVANT d'écrire : une entrée invalide échoue ici, rien n'atteint
      // Firestore. (Même discipline que le patron asset.)
      const fields = ResourceCreateInputSchema.parse(input);

      const actor = getActorId();
      const ref = doc(col);
      const now = new Date();

      // Champs métier optionnels : on ne les écrit que s'ils sont fournis
      // (évite d'ancrer des `undefined` / `null` parasites dans Firestore).
      const optional: DocumentData = {};
      if (fields.capacity !== undefined) optional['capacity'] = fields.capacity;
      if (fields.placeId !== undefined) optional['placeId'] = fields.placeId;
      if (fields.assetId !== undefined) optional['assetId'] = fields.assetId;
      if (fields.bookableOnline !== undefined) optional['bookableOnline'] = fields.bookableOnline;
      if (fields.setupMinutes !== undefined) optional['setupMinutes'] = fields.setupMinutes;
      if (fields.customFields !== undefined) optional['customFields'] = fields.customFields;

      const payload: DocumentData = {
        orgId: fields.orgId,
        type: fields.type,
        name: fields.name,
        ...optional,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: actor,
        updatedBy: actor,
        archivedAt: null,
        deletedAt: null,
        schemaVersion: CURRENT_SCHEMA_VERSION,
      };

      await setDoc(ref, payload as unknown as Resource);

      // setDoc ne résout pas serverTimestamp() : on valide une projection
      // cohérente avec ce qui vient d'être écrit.
      return ResourceSchema.parse({
        id: ref.id,
        ...payload,
        createdAt: now,
        updatedAt: now,
      });
    },

    async update(id: string, patch: ResourceUpdateInput): Promise<Resource> {
      // Valider AVANT d'ecrire (meme doctrine que create).
      const fields = ResourceUpdateInputSchema.parse(patch);
      const actor = getActorId();

      // Patch transforme pour updateDoc : customFields est eclate en chemins
      // pointes ('customFields.<cle>') pour fusionner cle par cle au lieu de
      // remplacer la map entiere (cf. buildUpdateData ; null = deleteField).
      const data = buildUpdateData(fields);
      data['updatedAt'] = serverTimestamp();
      data['updatedBy'] = actor;

      // Ref brute (sans converter) pour l'ecriture partielle : le converter
      // exigerait le document complet.
      await updateDoc(doc(db, RESOURCES_COLLECTION, id), data);

      // Relire le document reel via la ref convertie : source de verite.
      const snap = await getDoc(doc(col, id));
      if (!snap.exists()) {
        throw new Error(`resource ${id} introuvable apres mise a jour.`);
      }
      return snap.data();
    },

    async list(options?: ListOptions): Promise<Resource[]> {
      // Cadrage orgId, exclusion des archives, sous-type, tri, taille de page
      // et curseur : toute la semantique vit dans `runList` (list-query.ts),
      // ecrite une seule fois pour les 55 familles.
      return runList(col, orgId, options);
    },

    async count(options?: CountOptions): Promise<number> {
      // Compter sans charger : meme cadrage que `list`, sans page ni tri
      // (cf. runCount, list-query.ts). Un compteur calcule sur la page
      // courante ment des que la liste est paginee.
      return runCount(col, orgId, options);
    },

    async get(id: string): Promise<Resource | null> {
      const ref = doc(col, id);
      const snap = await getDoc(ref);
      if (!snap.exists()) return null;
      return snap.data();
    },

    // archive / unarchive / remove : cf. `archiveOps` plus haut.
    ...archiveOps,
  };
}
